import React, { useEffect, useState } from "react";
import { Store, Loader2, CheckCircle2, Link2, Unlink, Clock, AlertTriangle, RefreshCw, HelpCircle, ChevronDown, ChevronRight, Copy } from "lucide-react";
import { adminApi } from "../../lib/api";
import { useToast } from "../../hooks/use-toast";
import { Button } from "../ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const API_STATE = {
  ready: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  connected_no_location: { icon: CheckCircle2, cls: "bg-emerald-50 text-emerald-700 border-emerald-200", dot: "bg-emerald-500" },
  awaiting_approval: { icon: Clock, cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  reconnect: { icon: AlertTriangle, cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  unknown: { icon: AlertTriangle, cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  not_connected: { icon: Clock, cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" },
  not_configured: { icon: Clock, cls: "bg-slate-50 text-slate-600 border-slate-200", dot: "bg-slate-400" },
};

// Connection card shown above the blog list.
export function GoogleBusinessConnect() {
  const { toast } = useToast();
  const [status, setStatus] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [loadingAccts, setLoadingAccts] = useState(false);
  const [sel, setSel] = useState({ account: "", location: "", locationName: "" });
  const [saving, setSaving] = useState(false);
  const [apiState, setApiState] = useState(null);
  const [checking, setChecking] = useState(false);
  const [showGuide, setShowGuide] = useState(false);
  const redirectUri = `${window.location.origin}/api/gbp/oauth/callback`;

  const refresh = () => adminApi.gbp.status().then(setStatus).catch(() => setStatus(null));
  const checkApi = () => {
    setChecking(true);
    adminApi.gbp.apiStatus().then(setApiState).catch(() => setApiState(null)).finally(() => setChecking(false));
  };

  useEffect(() => {
    refresh();
    checkApi();
    const params = new URLSearchParams(window.location.search);
    if (params.get("gbp") === "connected") toast({ title: "Google Business Profile connected", description: "Now choose the location to post to." });
    if (params.get("gbp") === "error") toast({ title: "Couldn't connect to Google", description: "Please try again.", variant: "destructive" });
  }, []); // eslint-disable-line

  const connect = () => { window.location.href = adminApi.gbp.startUrl(); };

  const loadAccounts = async () => {
    setLoadingAccts(true);
    try {
      const res = await adminApi.gbp.accounts();
      if (res.error) {
        toast({ title: "Couldn't load your Google locations", description: res.error, variant: "destructive" });
        setAccounts(null);
        return;
      }
      setAccounts(res.accounts || []);
    } catch (e) {
      toast({ title: "Couldn't load your Google locations", description: e?.response?.data?.detail || "Please try again shortly.", variant: "destructive" });
    } finally { setLoadingAccts(false); }
  };

  const saveSelection = async () => {
    if (!sel.account || !sel.location) return;
    setSaving(true);
    try {
      await adminApi.gbp.select({ account_id: sel.account, location_id: sel.location, location_name: sel.locationName });
      toast({ title: "Location saved", description: "You can now post blog posts to Google." });
      refresh();
      checkApi();
      setAccounts(null);
    } catch { toast({ title: "Couldn't save selection", variant: "destructive" }); }
    finally { setSaving(false); }
  };

  const disconnect = async () => {
    await adminApi.gbp.disconnect();
    toast({ title: "Disconnected from Google Business Profile" });
    setAccounts(null);
    refresh();
    checkApi();
  };

  if (!status) return null;

  if (!status.configured) {
    return (
      <div data-testid="gbp-not-configured" className="mb-5 flex items-start gap-3 rounded-2xl border border-slate-100 bg-slate-50 px-5 py-4">
        <span className="w-9 h-9 rounded-xl bg-slate-200 text-slate-500 flex items-center justify-center shrink-0"><Store size={17} /></span>
        <div className="text-sm">
          <p className="font-display font-bold text-navy">Google Business posting — not set up yet</p>
          <p className="text-slate-500 mt-0.5">Once your Google Cloud OAuth keys are added and the Business Profile API is approved, you'll be able to post blog posts straight to your Google Business page from here.</p>
        </div>
      </div>
    );
  }

  const locations = (accounts || []).flatMap((a) => a.locations.map((l) => ({ ...l, account: a.id })));

  return (
    <div data-testid="gbp-connect-card" className="mb-5 rounded-2xl border border-slate-100 bg-white shadow-sm px-5 py-4">
      <div className="flex items-center gap-3 flex-wrap">
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 ${status.connected ? "bg-emerald-50 text-emerald-600" : "bg-slate-100 text-slate-500"}`}><Store size={17} /></span>
        <div className="flex-1 min-w-0">
          <p className="font-display font-bold text-navy flex items-center gap-2">
            Google Business Profile
            {status.connected && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
          </p>
          <p className="text-xs text-slate-500 mt-0.5" data-testid="gbp-connect-subtitle">
            {status.connected
              ? (status.location_name ? `Posting to: ${status.location_name}` : "Connected — choose a location to post to below.")
              : "Connect your Google account to post blog posts to your Google Business page."}
          </p>
        </div>
        {!status.connected ? (
          <Button data-testid="gbp-connect-btn" onClick={connect} className="bg-orange hover:bg-orange-dark text-white rounded-full"><Link2 size={15} className="mr-1.5" /> Connect Google Business Profile</Button>
        ) : (
          <div className="flex items-center gap-2">
            {!accounts && <Button data-testid="gbp-choose-location-btn" variant="outline" onClick={loadAccounts} disabled={loadingAccts} className="rounded-full border-slate-200">{loadingAccts ? <><Loader2 size={15} className="mr-1.5 animate-spin" /> Loading…</> : (status.location_name ? "Change location" : "Choose location")}</Button>}
            <Button data-testid="gbp-disconnect-btn" variant="outline" onClick={disconnect} className="rounded-full border-slate-200 text-red-500 hover:bg-red-50"><Unlink size={15} className="mr-1.5" /> Disconnect</Button>
          </div>
        )}
      </div>

      {apiState && (
        <div data-testid="gbp-api-status" className={`mt-4 flex items-start gap-3 rounded-xl border px-4 py-3 ${(API_STATE[apiState.state] || API_STATE.unknown).cls}`}>
          {React.createElement((API_STATE[apiState.state] || API_STATE.unknown).icon, { size: 18, className: "shrink-0 mt-0.5" })}
          <div className="flex-1 min-w-0 text-sm">
            <p className="font-bold flex items-center gap-2">
              Google API status: {apiState.label}
              <span className={`w-2 h-2 rounded-full ${(API_STATE[apiState.state] || API_STATE.unknown).dot}`} />
            </p>
            <p className="opacity-90 mt-0.5 leading-relaxed">{apiState.detail}</p>
          </div>
          <button data-testid="gbp-api-recheck" onClick={checkApi} disabled={checking} title="Re-check with Google" className="shrink-0 inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1.5 rounded-full bg-white/60 hover:bg-white disabled:opacity-50">
            <RefreshCw size={13} className={checking ? "animate-spin" : ""} /> Re-check
          </button>
        </div>
      )}

      <div className="mt-4 border-t border-slate-100 pt-3">
        <button data-testid="gbp-guide-toggle" onClick={() => setShowGuide((v) => !v)} className="inline-flex items-center gap-1.5 text-xs font-semibold text-navy hover:text-orange transition-colors">
          {showGuide ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          <HelpCircle size={14} /> Setup &amp; troubleshooting guide
        </button>
        {showGuide && (
          <div data-testid="gbp-guide" className="mt-3 space-y-4 text-sm text-slate-600 bg-slate-50 rounded-xl p-4 leading-relaxed">
            <p className="text-slate-500">Posting to Google needs two one-off approvals inside <b>Google Cloud Console</b> (console.cloud.google.com). These are on Google's side — nothing to change on the website.</p>

            <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-amber-800">
              <p className="font-bold">Access request submitted — awaiting Google approval</p>
              <p className="mt-0.5">Google case reference: <code className="font-semibold">5-3299000041711</code>. Approval typically takes a few days to ~2 weeks; posting starts working automatically once approved.</p>
            </div>

            <div>
              <p className="font-bold text-navy">1 · Get Business Profile API access (lifts your quota from 0)</p>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Open <b>APIs &amp; Services → Library</b> and <b>Enable</b>: Google My Business API, My Business Account Management API, My Business Business Information API.</li>
                <li>Request access via Google's form: <span className="text-orange break-all">support.google.com/business/contact/api_default</span> — give your Project ID and say you're posting your own updates to your own Business Profile.</li>
                <li>Wait for Google's approval email (a few days to ~2 weeks). Posting starts working automatically once approved.</li>
              </ol>
            </div>

            <div>
              <p className="font-bold text-navy">2 · Publish the OAuth consent screen (stops the weekly disconnect)</p>
              <ol className="list-decimal ml-5 mt-1 space-y-1">
                <li>Open <b>APIs &amp; Services → OAuth consent screen</b>.</li>
                <li>If Publishing status is <b>Testing</b>, click <b>PUBLISH APP</b> → confirm, so it becomes <b>In production</b>.</li>
                <li>Google may ask for app verification because the <code>business.manage</code> permission is sensitive. Completing this removes the 7-day reconnect.</li>
              </ol>
            </div>

            <div>
              <p className="font-bold text-navy">3 · Check the redirect URI</p>
              <p className="mt-1">In <b>APIs &amp; Services → Credentials → your OAuth client</b>, the Authorised redirect URI must be <b>exactly</b>:</p>
              <div className="mt-1.5 flex items-center gap-2 bg-white border border-slate-200 rounded-lg px-3 py-2">
                <code className="flex-1 break-all text-navy text-xs">{redirectUri}</code>
                <button data-testid="gbp-copy-redirect" onClick={() => { navigator.clipboard?.writeText(redirectUri); toast({ title: "Redirect URI copied" }); }} className="shrink-0 text-slate-400 hover:text-navy" title="Copy"><Copy size={14} /></button>
              </div>
            </div>

            <p className="text-slate-500">When the status badge above says <b>"Ready to post"</b>, the <b>Post to Google</b> button on any blog post will publish it to your Business Profile.</p>
          </div>
        )}
      </div>

      {accounts && (
        <div className="mt-4 flex items-end gap-3 flex-wrap border-t border-slate-100 pt-4">
          <div className="min-w-[260px]">
            <label className="text-xs font-semibold text-slate-500">Business location</label>
            <Select value={sel.location} onValueChange={(v) => { const loc = locations.find((l) => l.id === v); setSel({ account: loc?.account || "", location: v, locationName: loc?.title || "" }); }}>
              <SelectTrigger data-testid="gbp-location-select" className="mt-1"><SelectValue placeholder="Select a location" /></SelectTrigger>
              <SelectContent>
                {locations.map((l) => <SelectItem key={l.id} value={l.id}>{l.title || l.id}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button data-testid="gbp-save-location-btn" onClick={saveSelection} disabled={!sel.location || saving} className="bg-orange hover:bg-orange-dark text-white rounded-full">{saving ? "Saving…" : "Save location"}</Button>
          {locations.length === 0 && <p className="text-xs text-slate-400">No locations found on this Google account.</p>}
        </div>
      )}
    </div>
  );
}

// Per-blog-row button.
export function GbpPostButton({ item, onDone }) {
  const { toast } = useToast();
  const [posting, setPosting] = useState(false);
  const posted = !!item.gbp_posted_at;

  const post = async () => {
    setPosting(true);
    try {
      const res = await adminApi.gbp.postBlog(item.id);
      toast({ title: posted ? "Re-posted to Google" : "Posted to Google Business page", description: res.state ? `Status: ${res.state.toLowerCase()}` : "" });
      onDone && onDone();
    } catch (e) {
      toast({ title: "Couldn't post to Google", description: e?.response?.data?.detail || "Please try again.", variant: "destructive" });
    } finally { setPosting(false); }
  };

  return (
    <button
      data-testid={`gbp-post-btn-${item.id}`}
      onClick={post}
      disabled={posting}
      title={posted ? "Already posted — click to post again" : "Post to Google Business page"}
      className={`h-9 px-3 rounded-lg flex items-center gap-1.5 text-xs font-semibold transition-colors disabled:opacity-60 ${posted ? "bg-emerald-50 text-emerald-700 hover:bg-emerald-100" : "bg-slate-100 text-navy hover:bg-slate-200"}`}
    >
      {posting ? <Loader2 size={14} className="animate-spin" /> : posted ? <CheckCircle2 size={14} /> : <Store size={14} />}
      {posting ? "Posting…" : posted ? "Posted" : "Post to Google"}
    </button>
  );
}
